/**
 * Persistent floating "Install Law OS" pill.
 *
 * Show when ALL of:
 *   • App is NOT already installed (display-mode != standalone, no iOS standalone flag,
 *     and localStorage 'pwa_installed' is unset)
 *   • Browser has dispatched `beforeinstallprompt` (Chrome / Edge / Android)
 *     OR iOS Safari (we surface manual "Add to Home Screen" hint)
 *   • User has not dismissed it during the current session
 *
 * Close button hides for the current browser session only — pill returns on next visit
 * until the app is actually installed, at which point `appinstalled` fires and we set the
 * permanent flag.
 */
import { useEffect, useState } from "react";
import { Smartphone, X, Share } from "lucide-react";
import { toast } from "sonner";

const INSTALLED_KEY = "pwa_installed";
const SESSION_DISMISS_KEY = "pwa_install_dismissed_session";

function isStandalone() {
    try {
        if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
        if (window.navigator.standalone === true) return true;   // iOS Safari
    } catch { /* ignore */ }
    return false;
}

function isIOS() {
    return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

export default function PWAInstallPill() {
    const [prompt, setPrompt] = useState(null);
    const [iosHint, setIosHint] = useState(false);
    const [show, setShow] = useState(false);

    useEffect(() => {
        // Already installed? Never show.
        if (isStandalone()) {
            try { localStorage.setItem(INSTALLED_KEY, "1"); } catch { /* ignore */ }
            return;
        }
        try {
            if (localStorage.getItem(INSTALLED_KEY) === "1") return;
            if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") return;
        } catch { /* ignore */ }

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setPrompt(e);
            setShow(true);
        };
        const onInstalled = () => {
            try { localStorage.setItem(INSTALLED_KEY, "1"); } catch { /* ignore */ }
            setPrompt(null);
            setShow(false);
            toast.success("Law OS installed — open it from your home screen anytime.");
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);

        // iOS Safari fallback — no beforeinstallprompt event; surface the manual hint.
        if (isIOS() && !isStandalone()) {
            const t = setTimeout(() => { setIosHint(true); setShow(true); }, 0);
            return () => {
                clearTimeout(t);
                window.removeEventListener("beforeinstallprompt", onBeforeInstall);
                window.removeEventListener("appinstalled", onInstalled);
            };
        }

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const dismiss = () => {
        try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* ignore */ }
        setShow(false);
    };

    const install = async () => {
        if (iosHint) {
            toast.message(
                "Tap the Share icon in Safari, then 'Add to Home Screen' to install Law OS.",
                { duration: 6000 }
            );
            return;
        }
        if (!prompt) return;
        try {
            prompt.prompt();
            const choice = await prompt.userChoice;
            if (choice?.outcome === "accepted") {
                try { localStorage.setItem(INSTALLED_KEY, "1"); } catch { /* ignore */ }
                setShow(false);
            }
        } catch { /* user dismissed native prompt */ }
        setPrompt(null);
    };

    if (!show) return null;
    return (
        <div
            className="fixed bottom-4 left-4 z-[100] flex items-center gap-2 bg-card border border-primary/40 shadow-2xl shadow-black/60 pl-3 pr-1 py-1.5 max-w-[calc(100vw-2rem)]"
            data-testid="pwa-install-pill"
        >
            <div className="flex items-center gap-2 text-xs text-foreground">
                {iosHint ? (
                    <Share className="w-3.5 h-3.5 text-primary" strokeWidth={1.5}/>
                ) : (
                    <Smartphone className="w-3.5 h-3.5 text-primary" strokeWidth={1.5}/>
                )}
                <span className="hidden sm:inline">
                    {iosHint ? "Add Law OS to your home screen" : "Install Law OS"}
                </span>
                <span className="sm:hidden">Install</span>
            </div>
            <button
                onClick={install}
                data-testid="pwa-install-pill-cta"
                className="ml-1 inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground hover:bg-[#C9A537] text-[10px] font-semibold uppercase tracking-[0.15em]"
            >
                {iosHint ? "How" : "Install"}
            </button>
            <button
                onClick={dismiss}
                data-testid="pwa-install-pill-close"
                className="p-1.5 text-[#7A7A7A] hover:text-foreground"
                aria-label="Dismiss for this session"
                title="Hide for this session"
            >
                <X className="w-3.5 h-3.5" strokeWidth={1.5}/>
            </button>
        </div>
    );
}
